import React, { useState, useEffect } from "react";
import defaultImage from "../assets/image_not_found.jpg";
import { fetchSubmodelElement } from "../services/DppManagerService";
import Config from "../config/Config";
import getLocalizedText from "./Utils";
import LoadingDots from "./LoadingDots";

const IMAGE_NOT_FOUND = defaultImage;

// Define propertyMap for image
const PROPERTY_MAP = {
  submodelIdShort: "PublicInformation",
  path: "GeneralInformation.ProductImage",
  key: "productImage",
};

function DppProductImage({ submodelDescriptors }) {
  const { mainColor } = Config.appearance;

  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const loadImage = async () => {
      if (!submodelDescriptors?.length) {
        setLoading(false);
        return;
      }

      setLoading(true);
      const descriptor = submodelDescriptors.find(
        (d) => d.idShort === PROPERTY_MAP.submodelIdShort
      );
      if (!descriptor) {
        if (!signal.aborted) {
          setImageSrc(IMAGE_NOT_FOUND);
          setError(true);
          setLoading(false);
        }
        return;
      }

      try {
        const element = await fetchSubmodelElement(descriptor, PROPERTY_MAP.path, { signal });
        if (!signal.aborted) {
          const imageUrl = element?.value ?? element?.file?.path ?? element?.url ?? null;
          const displayUrl = getLocalizedText(imageUrl);
          setImageSrc(displayUrl || IMAGE_NOT_FOUND);
          setError(!displayUrl);
          setLoading(false);
        }
      } catch (err) {
        if (signal.aborted) return;
        console.error("Failed to fetch image:", err);
        setImageSrc(IMAGE_NOT_FOUND);
        setError(true);
        setLoading(false);
      }
    };

    loadImage();
    return () => controller.abort();
  }, [submodelDescriptors]);

  const handleError = () => {
    setError(true);
    setImageSrc(IMAGE_NOT_FOUND);
  };

  if (loading) {
    return <LoadingDots color={mainColor} />;
  }

  if (!imageSrc || (imageSrc === IMAGE_NOT_FOUND && !error)) {
    return null; // Display nothing if imageSrc is null or invalid without error
  }

  return (
    <img
      src={imageSrc}
      alt='product-image'
      style={{ maxHeight: "100%", maxWidth: "260px", borderRadius: "20px"}}
      onError={handleError}
    />
  );
}

export default DppProductImage;